// Pure reducer + types for the live workout session. Split from the React
// provider in LiveSessionContext.tsx so the state transitions can be unit-
// tested without React Query / fetch / context plumbing.

import type { WeightUnit } from '@/api/types';

export const DEFAULT_REST_SEC = 90;

/* Inactivity thresholds for lift sessions. A "stale" session that the user
   forgot to end pollutes their streak + duration stats, so after a long idle
   period we warn first, then auto-end. Cardio sessions are unaffected — they
   have an explicit running timer the user pauses/resumes deliberately.

   25 min covers normal between-set rest (1–3 min) and even generous power-
   lift rest (5–8 min) with plenty of headroom; if you haven't done anything
   for 25 minutes you're probably done. Auto-end fires at 40 min — gives the
   user ~15 min to dismiss the warning by logging another set. */
export const LIFT_IDLE_WARN_MS = 25 * 60 * 1000;
export const LIFT_IDLE_AUTO_END_MS = 40 * 60 * 1000;

export interface SessionSet {
  reps: number;
  weight: number | null;
  rpe: number | null;
  loggedAt: string;
  saved: boolean;
}

export interface SessionExercise {
  uid: string;
  exerciseId: string | null;
  exerciseName: string;
  bodyweight: boolean;
  muscleGroups: string[];
  sets: SessionSet[];
  notes: string;
}

export type SessionKind = 'lift' | 'cardio';

export interface LiveSession {
  workoutId: string | null;
  kind: SessionKind;
  startedAt: string;
  /* Last user interaction. Used to trigger the lift-session idle warning +
     auto-end. Bumped by the reducer on any action that represents real
     activity (logging sets, adding/removing exercises, edits, etc.). */
  lastActivityAt: string;
  notes: string;
  weightUnit: WeightUnit;
  restDefaultSec: number;
  restEndsAt: string | null;

  exercises: SessionExercise[];

  cardioActivity: string | null;
  cardioElapsedSec: number;
  cardioRunningSince: string | null;
  cardioDistance: number | null;
}

export type LiveSessionAction =
  | { type: 'start'; kind: SessionKind; weightUnit: WeightUnit; cardioActivity?: string; nowIso?: string }
  | { type: 'hydrate'; session: LiveSession | null }
  | { type: 'attachWorkoutId'; workoutId: string }
  | { type: 'addExercise'; exercise: SessionExercise }
  | { type: 'removeExercise'; uid: string }
  | { type: 'setExerciseNotes'; uid: string; notes: string }
  | { type: 'logSet'; uid: string; set: SessionSet; restSec: number; nowMs?: number }
  | { type: 'markSetSaved'; uid: string; setIdx: number }
  | { type: 'editSet'; uid: string; setIdx: number; reps: number; weight: number | null; rpe: number | null }
  | { type: 'removeSet'; uid: string; setIdx: number }
  | { type: 'cardioToggle'; nowIso?: string; nowMs?: number }
  | { type: 'cardioSetDistance'; distance: number | null }
  | { type: 'setNotes'; notes: string }
  | { type: 'skipRest' }
  | { type: 'extendRest'; sec: number; nowMs?: number }
  | { type: 'finish' };

export interface LiveSessionState {
  active: LiveSession | null;
}

/* Action types that count as user activity (i.e. they refute "stale session"
   inactivity). Hydrate/finish are explicitly excluded — hydrate restores the
   prior lastActivityAt unchanged, finish ends the session. */
const ACTIVITY_ACTION_TYPES: ReadonlySet<LiveSessionAction['type']> = new Set([
  'attachWorkoutId',
  'addExercise',
  'removeExercise',
  'setExerciseNotes',
  'logSet',
  'markSetSaved',
  'editSet',
  'removeSet',
  'cardioToggle',
  'cardioSetDistance',
  'setNotes',
  'skipRest',
  'extendRest',
]);

function bumpActivity(state: LiveSessionState, action: LiveSessionAction): LiveSessionState {
  if (!state.active) return state;
  if (!ACTIVITY_ACTION_TYPES.has(action.type)) return state;
  const nowIso =
    'nowMs' in action && typeof action.nowMs === 'number'
      ? new Date(action.nowMs).toISOString()
      : 'nowIso' in action && typeof action.nowIso === 'string'
        ? action.nowIso
        : new Date().toISOString();
  return { active: { ...state.active, lastActivityAt: nowIso } };
}

export function liveSessionReducer(state: LiveSessionState, action: LiveSessionAction): LiveSessionState {
  const next = applyAction(state, action);
  return bumpActivity(next, action);
}

function applyAction(state: LiveSessionState, action: LiveSessionAction): LiveSessionState {
  switch (action.type) {
    case 'hydrate': {
      if (!action.session) return { active: null };
      // Legacy sessions persisted before lastActivityAt existed default to
      // startedAt so the idle clock doesn't insta-fire on first load.
      const lastActivityAt = action.session.lastActivityAt ?? action.session.startedAt;
      return { active: { ...action.session, lastActivityAt } };
    }
    case 'start': {
      const startedAt = action.nowIso ?? new Date().toISOString();
      const session: LiveSession = {
        workoutId: null,
        kind: action.kind,
        startedAt,
        lastActivityAt: startedAt,
        notes: '',
        weightUnit: action.weightUnit,
        restDefaultSec: DEFAULT_REST_SEC,
        restEndsAt: null,
        exercises: [],
        cardioActivity: action.cardioActivity ?? null,
        cardioElapsedSec: 0,
        cardioRunningSince: action.kind === 'cardio' ? startedAt : null,
        cardioDistance: null,
      };
      return { active: session };
    }
    case 'attachWorkoutId':
      return state.active ? { active: { ...state.active, workoutId: action.workoutId } } : state;
    case 'addExercise':
      return state.active
        ? { active: { ...state.active, exercises: [...state.active.exercises, action.exercise] } }
        : state;
    case 'removeExercise':
      return state.active
        ? { active: { ...state.active, exercises: state.active.exercises.filter((e) => e.uid !== action.uid) } }
        : state;
    case 'setExerciseNotes':
      return state.active
        ? {
            active: {
              ...state.active,
              exercises: state.active.exercises.map((e) =>
                e.uid === action.uid ? { ...e, notes: action.notes } : e,
              ),
            },
          }
        : state;
    case 'logSet': {
      if (!state.active) return state;
      const baseMs = action.nowMs ?? Date.now();
      const restEndsAt = new Date(baseMs + action.restSec * 1000).toISOString();
      return {
        active: {
          ...state.active,
          restEndsAt,
          exercises: state.active.exercises.map((e) =>
            e.uid === action.uid ? { ...e, sets: [...e.sets, action.set] } : e,
          ),
        },
      };
    }
    case 'markSetSaved':
      return state.active
        ? {
            active: {
              ...state.active,
              exercises: state.active.exercises.map((e) =>
                e.uid === action.uid
                  ? { ...e, sets: e.sets.map((s, i) => (i === action.setIdx ? { ...s, saved: true } : s)) }
                  : e,
              ),
            },
          }
        : state;
    case 'editSet':
      return state.active
        ? {
            active: {
              ...state.active,
              exercises: state.active.exercises.map((e) =>
                e.uid === action.uid
                  ? {
                      ...e,
                      sets: e.sets.map((s, i) =>
                        i === action.setIdx
                          ? { ...s, reps: action.reps, weight: action.weight, rpe: action.rpe, saved: false }
                          : s,
                      ),
                    }
                  : e,
              ),
            },
          }
        : state;
    case 'removeSet':
      return state.active
        ? {
            active: {
              ...state.active,
              exercises: state.active.exercises.map((e) =>
                e.uid === action.uid ? { ...e, sets: e.sets.filter((_, i) => i !== action.setIdx) } : e,
              ),
            },
          }
        : state;
    case 'cardioToggle': {
      if (!state.active || state.active.kind !== 'cardio') return state;
      if (state.active.cardioRunningSince) {
        const baseMs = action.nowMs ?? Date.now();
        const ranSec = Math.max(0, (baseMs - new Date(state.active.cardioRunningSince).getTime()) / 1000);
        return {
          active: {
            ...state.active,
            cardioElapsedSec: state.active.cardioElapsedSec + ranSec,
            cardioRunningSince: null,
          },
        };
      }
      return { active: { ...state.active, cardioRunningSince: action.nowIso ?? new Date().toISOString() } };
    }
    case 'cardioSetDistance':
      return state.active ? { active: { ...state.active, cardioDistance: action.distance } } : state;
    case 'setNotes':
      return state.active ? { active: { ...state.active, notes: action.notes } } : state;
    case 'skipRest':
      return state.active ? { active: { ...state.active, restEndsAt: null } } : state;
    case 'extendRest': {
      if (!state.active) return state;
      const fallback = action.nowMs ?? Date.now();
      const baseMs = state.active.restEndsAt ? new Date(state.active.restEndsAt).getTime() : fallback;
      return { active: { ...state.active, restEndsAt: new Date(baseMs + action.sec * 1000).toISOString() } };
    }
    case 'finish':
      return { active: null };
    default:
      return state;
  }
}

/// <summary>Aggregates muscle groups across logged exercises into the comma-joined kind
/// the API stores on the parent WorkoutEntry. Falls back to "lift" / "cardio" if no
/// muscle groups were captured.</summary>
export function deriveFinishKind(session: LiveSession): string {
  if (session.kind === 'cardio') return 'cardio';
  const tokens = new Set<string>();
  for (const ex of session.exercises) {
    for (const g of ex.muscleGroups) tokens.add(g);
  }
  return tokens.size > 0 ? Array.from(tokens).join(',') : 'lift';
}
